// La primera pantalla que ve alguien que acaba de registrarse.
//
// ─────────────────────────────────────────────────────────────────
// POR QUÉ EXISTE
//
// Hasta acá, un usuario nuevo entraba a un armario vacío sin nada que
// lo guiara. Las piezas para configurarlo bien YA existían —el sembrado
// de ocasiones, el país, ahora el estilo— pero vivían escondidas en el
// perfil, que es el último lugar donde alguien mira el primer día.
//
// Y hay una cosa que solo se puede resolver acá: EL ARRANQUE EN FRÍO.
// El estilo de una persona se puede deducir contando su armario, y esa
// es la mejor forma de saberlo. Pero un usuario nuevo no tiene armario.
// No hay nada que contar. Preguntarlo es la única entrada posible el
// primer día, y el conteo llega después — para poder decirle "dijiste
// elegante y tu armario dice deportivo", que es una conversación mucho
// más valiosa y que solo se puede tener si antes preguntaste.
//
// ─────────────────────────────────────────────────────────────────
// TRES PASOS, Y CADA UNO ARREGLA ALGO QUE SIN ÉL FALLA EN SILENCIO
//
//   PAÍS    decide qué estación es HOY. En agosto es invierno en Buenos
//           Aires y verano en Madrid: sin este dato la app le sugiere
//           lino en julio a media humanidad, y nadie entiende por qué.
//
//   ROPA    qué guardarropa usa. ORDENA el desplegable de 108 prendas y
//           dirige los ejemplos de estilo del último paso. Se pregunta
//           por la ROPA y no por el género: son cosas distintas para una
//           parte real de la gente, y la app necesita la primera.
//
//   DÍA     siembra las ocasiones. Sin ellas no hay regla de formalidad
//           —una prenda sin ocasión no tiene rango y se saltea la regla
//           entera— y las sugerencias salen peores sin ningún aviso.
//
//   ESTILO  ordena las sugerencias desde la primera prenda cargada.
//           Es el único de los tres que, si falta, solo hace que la app
//           sea menos buena en vez de estar mal.
//
// Por eso los tres se pueden saltear pero el estilo es el que menos
// duele: el botón de saltear está, y lo que se pierde está dicho.
//
// ─────────────────────────────────────────────────────────────────
// UNA SOLA LLAMADA AL SERVIDOR
//
// Los países, los paquetes de ocasiones y los siete estilos vienen
// TODOS en `GET /api/perfil`. Tres pedidos en la primera pantalla que
// ve un usuario nuevo son tres oportunidades de que algo tarde, y la
// primera impresión de una app se decide en esos segundos.

import { useEffect, useRef, useState } from "react";
import * as api from "./api";
import { invalidarOpciones } from "./opciones";
import { IDIOMAS, idiomaActual, ponerIdioma, t, tCat, tError, useIdioma } from "./i18n";
/* ⚠️  SE IMPORTA CON OTRO NOMBRE, Y ESO ARREGLA UNA PANTALLA EN BLANCO.
 *
 * Se llamaba `pais`, igual que el estado `const [pais, setPais]` de más
 * abajo. Dentro del componente, el estado TAPA el import: `pais` deja
 * de ser la función y pasa a ser el string del país elegido. Entonces
 * la línea que arma el desplegable —`pais(p.codigo, p.nombre)`— llamaba
 * a un string. `pais is not a function`, React desmonta el árbol, y lo
 * que ve el usuario es una página en blanco.
 *
 * Lo caro es DÓNDE: esta es la primera pantalla de todo el que se
 * registra, y ninguna cuenta vieja la vuelve a ver. Estuvo rota sin que
 * nadie lo notara hasta que el dueño creó una cuenta de prueba el
 * 2026-09-05.
 *
 * Y no lo veía ningún guardián. `no-undef` no se queja: el nombre SÍ
 * existe. `no-use-before-define` tampoco: el uso está después de la
 * declaración. Es una tercera pregunta —¿algo local tapa un import?—
 * que ahora hace test_orden_declaraciones.js §4. */
import { pais as etiquetaPais } from "./paises";

const PASOS = ["pais", "ropa", "dia", "estilo"];

export default function PrimerUso({ onListo }) {
  // Re-renderiza si cambia el idioma.
  useIdioma();
  // Los países, los paquetes de ocasiones y los siete estilos vienen
  // TODOS en `GET /api/perfil`. Una sola llamada, y por eso el pedido
  // se hace acá adentro en vez de recibirlo por props: la pantalla es
  // dueña de lo que necesita.
  const [opciones, setOpciones] = useState(null);
  const [paso, setPaso] = useState(0);
  const [pais, setPais] = useState("");
  const [ropa, setRopa] = useState(null);
  const [paquete, setPaquete] = useState(null);
  const [dominante, setDominante] = useState(null);
  const [acentos, setAcentos] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  /* ⚠️  EL IDIOMA NO SE GUARDA EN ESTADO, SE LEE. (Corregido el 10/9,
   *     el mismo día que se agregó el selector.)
   *
   * La primera versión hacía `useState(idiomaActual)`: una FOTO del
   * idioma al montar. Y esta pantalla se monta mientras `/api/auth/me`
   * todavía está viajando — cuando la respuesta llega, `AuthContext`
   * aplica el idioma del perfil y el idioma real cambia. La foto no.
   *
   * El resultado fue una pantalla que decía «English» en el selector y
   * «¿Dónde vives?» en el título, las dos cosas al mismo tiempo. Un
   * instrumento que miente es peor que no tener instrumento: este
   * selector existe justamente para hacer visible el idioma real.
   *
   * `useIdioma()` de arriba repinta cuando el idioma cambia, así que
   * leerlo en cada render es correcto y siempre está al día. */
  const idioma = idiomaActual();

  /* Con qué idioma se ENTRÓ. Sirve para no mandar un PATCH cuando la
   * persona no tocó el desplegable: el perfil ya dice eso, y una
   * escritura de más es una oportunidad de más de que algo falle.
   * Es un ref y no estado porque no tiene que provocar ningún repintado
   * y no puede cambiar nunca durante esta pantalla. */
  const idiomaInicial = useRef(idiomaActual());

  useEffect(() => {
    let vivo = true;
    api
      .leerPerfil()
      .then((r) => {
        if (!vivo) return;
        setOpciones(r.opciones);
        // Si el país ya estaba puesto —por ejemplo porque el usuario
        // pasó por el perfil antes— se respeta en vez de pedirlo de
        // nuevo.
        if (r.perfil?.pais) setPais(r.perfil.pais);
      })
      .catch((e) => {
        // La bienvenida no puede ser una pared. Si no se pueden leer las
        // opciones, se dice y se deja salir: quedarse trabado acá es
        // peor que arrancar sin configurar.
        if (vivo)
          setError(
            t("inicio.errorOpciones", "No se pudieron cargar las opciones: {mensaje}", {
              mensaje: tError(e),
            })
          );
      });
    return () => {
      vivo = false;
    };
  }, []);

  /* LOS EJEMPLOS SE PIDEN DE NUEVO CUANDO CAMBIA LA ROPA.
   *
   * El servidor los arma según el guardarropa del PERFIL, que todavía
   * no se guardó —se guarda todo junto al final— así que la primera
   * carga trae los mezclados. Al elegir, se vuelve a pedir con la
   * respuesta puesta.
   *
   * Es una llamada de más en el único momento en que se justifica: la
   * alternativa era mandar las tres variantes al navegador para usar
   * una, o mostrarle a alguien que usa ropa de hombre una tarjeta con
   * blusa y sandalia después de haber contestado que no.
   */
  useEffect(() => {
    if (!ropa) return;
    let vivo = true;
    api
      .leerPerfil({ guardarropa: ropa })
      .then((r) => {
        if (vivo && r.opciones?.estilos) {
          setOpciones((o) => ({ ...o, estilos: r.opciones.estilos }));
        }
      })
      .catch(() => {
        // Si falla, quedan los ejemplos mezclados, que funcionan. No es
        // un error que valga la pena mostrarle a nadie.
      });
    return () => {
      vivo = false;
    };
  }, [ropa]);

  const actual = PASOS[paso];

  /**
   * Guarda todo junto al final, y no paso por paso.
   *
   * Guardar en cada paso parece más seguro y es peor: alguien que
   * abandona en el paso 2 queda con el país puesto y sin ocasiones, que
   * es un estado que nadie eligió. Con un solo guardado al final, o
   * está todo o no está nada, y "no está nada" es exactamente lo que
   * significa saltear.
   *
   * ⚠️  EL IDIOMA ES LA EXCEPCIÓN, Y TIENE QUE SERLO. Saltear significa
   *     "no configuré nada", pero el idioma **ya está configurado**: la
   *     persona lo está leyendo en esta misma pantalla. Dejarlo afuera
   *     del guardado haría que la app volviera al idioma anterior en el
   *     próximo dispositivo, sin que nadie lo haya pedido. Por eso va
   *     también cuando se saltea, y va SOLO: no arrastra ningún otro
   *     campo a medio contestar.
   */
  async function terminar(salteando) {
    setGuardando(true);
    setError("");
    try {
      if (salteando) {
        if (idioma && idioma !== idiomaInicial.current) {
          await api.guardarPerfil({ idioma });
        }
      } else {
        // El país y el estilo van juntos: son el mismo PATCH.
        const cambios = {};
        if (idioma && idioma !== idiomaInicial.current) cambios.idioma = idioma;
        if (pais) cambios.pais = pais;
        if (ropa) cambios.guardarropa = ropa;
        if (dominante) {
          cambios.estilo_dominante = dominante;
          cambios.estilos_acento = acentos;
        }
        if (Object.keys(cambios).length) await api.guardarPerfil(cambios);

        // Las ocasiones son otra ruta, y va después: si el sembrado
        // falla, el perfil ya quedó bien y se puede reintentar solo eso.
        if (paquete) {
          await api.sembrarOcasiones(paquete);
        }

        // Se tira el caché de opciones si cambió CUALQUIERA de las dos
        // cosas que lo componen:
        //
        //   - las ocasiones sembradas, que son los filtros de todas las
        //     pantallas;
        //   - el guardarropa, que ordena el desplegable de 108 prendas.
        //
        // Sin esto, el usuario elige "ropa de mujer" en la bienvenida y
        // el primer desplegable que abre sigue mostrando el orden
        // neutro hasta que recargue la página. Se ve igual que "no se
        // guardó", que es el reporte que ya llegó dos veces por otras
        // causas.
        if (paquete || ropa) invalidarOpciones();
      }

      // Se marca SIEMPRE, también al saltear. Una bienvenida que vuelve
      // en cada sesión deja de ser una bienvenida.
      await api.marcarPrimerUso();
      onListo?.();
    } catch (e) {
      setError(tError(e));
      setGuardando(false);
    }
  }

  /* Cambiar el idioma se ve AL INSTANTE, antes de guardar nada.
   *
   * No es una preferencia que se confirma al final como el país o el
   * estilo: es lo que la persona está leyendo mientras contesta las
   * otras tres preguntas. Guardarlo recién en el último paso significa
   * hacerle contestar toda la bienvenida en el idioma equivocado. */
  function cambiarIdioma(codigo) {
    ponerIdioma(codigo);
  }

  function alternarAcento(clave) {
    setAcentos((a) => {
      if (a.includes(clave)) return a.filter((x) => x !== clave);
      // Dos como máximo: es la receta del sistema, un dominante y uno o
      // dos acentos. Con cuatro no hay blend, hay lista de deseos.
      if (a.length >= 2) return a;
      return [...a, clave];
    });
  }

  const puedeSeguir =
    (actual === "pais" && pais) ||
    (actual === "ropa" && ropa) ||
    (actual === "dia" && paquete) ||
    (actual === "estilo" && dominante);

  if (!opciones && !error) {
    return (
      <div className="centrado">
        <p className="tenue">{t("comun.cargando", "Cargando...")}</p>
      </div>
    );
  }

  return (
    <div className="primer-uso">
      <div className="primer-uso-caja">
        <div className="primer-uso-pasos">
          {PASOS.map((p, i) => (
            <span
              key={p}
              className={`punto ${i === paso ? "punto-activo" : ""} ${i < paso ? "punto-hecho" : ""}`}
              aria-label={t("inicio.pasoDeN", "Paso {n} de {total}", { n: i + 1, total: PASOS.length })}
            />
          ))}
        </div>

        {error && <div className="mensaje-error" role="alert">{error}</div>}

        {actual === "pais" && (
          <>
            <h2>{t("inicio.dondeVives", "¿Dónde vives?")}</h2>
            <p className="tenue">
              {t("inicio.dondeVivesExplica", "Es lo único que le dice a la app qué estación es hoy. En agosto es invierno en Buenos Aires y verano en Madrid.")}
            </p>
            <select
              className="primer-uso-select"
              aria-label={t("inicio.eligePais", "Elige tu país")}
              value={pais}
              onChange={(e) => setPais(e.target.value)}
              autoFocus
            >
              <option value="">{t("inicio.eligePais", "Elige tu país")}</option>
              {(opciones?.paises || []).map((p) => (
                <option key={p.codigo} value={p.codigo}>
                  {etiquetaPais(p.codigo, p.nombre)}
                </option>
              ))}
            </select>

            {/* ⚠️  EL SELECTOR DE IDIOMA, ACÁ, Y NO ES REDUNDANTE CON EL
                DE LA PORTADA (2026-09-10).

                Entre elegir idioma en la portada y llegar a esta
                pantalla pasan dos cosas que pueden cambiarlo sin que
                nadie lo pida: el alta guarda `perfiles.idioma`, y al
                entrar `AuthContext` aplica ESE idioma. Si en el medio
                algo se pierde, la persona queda mirando una app en un
                idioma que no eligió, **en la única pantalla que no
                vuelve a ver nunca** — y sin ninguna forma de arreglarlo
                sin terminar la bienvenida primero.

                Además hace visible el estado: el idioma que la app cree
                tener deja de ser algo que hay que deducir mirando qué
                palabras salieron traducidas. Cuando se reportó esto, esa
                deducción fue justamente lo que faltó para diagnosticarlo.

                La etiqueta reusa `login.idioma`, que ya existe en los
                doce: es la misma palabra y no hay motivo para traducir
                "Idioma" dos veces. */}
            <div className="primer-uso-idioma">
              <label className="filtro-titulo" htmlFor="primer-uso-idioma">
                {t("login.idioma", "Idioma")}
              </label>
              <select
                id="primer-uso-idioma"
                className="primer-uso-select"
                value={idioma}
                onChange={(e) => cambiarIdioma(e.target.value)}
              >
                {/* Cada uno EN SU PROPIO IDIOMA: alguien que abrió la
                    app en el idioma equivocado no puede leer la lista si
                    está escrita en el idioma equivocado. Mismo criterio
                    que el selector de la portada. */}
                {IDIOMAS.map((i) => (
                  <option key={i.codigo} value={i.codigo}>
                    {i.nativo}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {actual === "ropa" && (
          <>
            <h2>{t("inicio.queRopa", "¿Qué ropa usas?")}</h2>
            {/* Se pregunta por la ROPA y no por el género, y no es un
                eufemismo: es lo que la app necesita saber. Son cosas
                distintas para un hombre que usa faldas, para una mujer
                que solo usa sastrería masculina, para alguien no
                binario. Y esta pregunta la contesta todo el mundo sin
                incomodarse.

                Se dice QUÉ hace con la respuesta, y sobre todo qué NO
                hace: nada desaparece. */}
            <p className="tenue">
              {t("inicio.queRopaExplica", "Sirve para ordenar las listas: lo que usas aparece primero. Nada se esconde — vas a poder cargar cualquier prenda igual.")}
            </p>
            <div className="primer-uso-opciones">
              {(opciones?.guardarropas || []).map((g) => (
                <button
                  key={g.valor}
                  type="button"
                  className={`tarjeta-opcion ${ropa === g.valor ? "tarjeta-elegida" : ""}`}
                  aria-pressed={ropa === g.valor}
                  onClick={() => setRopa(g.valor)}
                >
                  <strong>{t(`opciones.guardarropa.${g.valor}`, g.etiqueta)}</strong>
                </button>
              ))}
            </div>
          </>
        )}

        {actual === "dia" && (
          <>
            <h2>{t("inicio.comoEsTuDia", "¿Cómo es tu día?")}</h2>
            <p className="tenue">
              {t("inicio.comoEsTuDiaExplica", "Con esto la app arma tu lista de ocasiones. Después la cambias cuando quieras.")}
            </p>
            <div className="primer-uso-opciones">
              {(opciones?.paquetes || []).map((p) => (
                <button
                  key={p.clave}
                  type="button"
                  className={`tarjeta-opcion ${paquete === p.clave ? "tarjeta-elegida" : ""}`}
                  /* ⚠️  `aria-pressed` NO es decoración: es la única forma
                     de que "está elegida" exista fuera del CSS. El 8/9 la
                     marca visual se perdió contra un `:hover` pegado y no
                     quedaba NADA que dijera cuál estaba elegida — ni para
                     un lector de pantalla ni para una prueba. */
                  aria-pressed={paquete === p.clave}
                  onClick={() => setPaquete(p.clave)}
                >
                  <strong>{t(`opciones.paquete.${p.clave}`, p.etiqueta)}</strong>
                  <span className="tenue texto-mini">{t(`opciones.paqueteDetalle.${p.clave}`, p.detalle)}</span>
                  <span className="tenue texto-mini">
                    {t("inicio.cuantasOcasiones", "{n} ocasiones", { n: p.cuantas })}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {actual === "estilo" && (
          <>
            <h2>{t("inicio.conCualTeVes", "¿Con cuál te ves más?")}</h2>
            {/* Se describe con PRENDAS y no con adjetivos. "¿Sos dramático
                o creativo?" no lo puede contestar alguien que nunca oyó
                hablar del sistema; "¿camisa de vestir y mocasín, o camisa
                estampada y bota militar?" se contesta mirando el placard. */}
            <p className="tenue">
              {t("inicio.estiloExplica", "Elige el que más te representa. Con eso la app ordena las sugerencias — no descarta ninguna.")}
            </p>

            <div className="primer-uso-opciones">
              {(opciones?.estilos || []).map((e) => {
                const esDominante = dominante === e.clave;
                const esAcento = acentos.includes(e.clave);
                return (
                  <button
                    key={e.clave}
                    type="button"
                    className={`tarjeta-opcion ${esDominante ? "tarjeta-elegida" : ""} ${esAcento ? "tarjeta-acento" : ""}`}
                    aria-pressed={esDominante || esAcento}
                    onClick={() => {
                      if (!dominante || esDominante) {
                        setDominante(esDominante ? null : e.clave);
                        setAcentos((a) => a.filter((x) => x !== e.clave));
                      } else {
                        alternarAcento(e.clave);
                      }
                    }}
                  >
                    {/* Ver el comentario largo en Perfil.js: la
                        traducción de los estilos existía en los doce
                        idiomas y ninguna pantalla la llamaba. */}
                    <strong>{t(`estilo.${e.clave}.nombre`, e.nombre)}</strong>
                    <span className="tenue texto-mini">
                      {t(`estilo.${e.clave}.mensaje`, e.mensaje)}
                    </span>
                    <span className="texto-mini">
                      {(e.ejemplos || []).map((x) => tCat("prenda", x)).join(" · ")}
                    </span>
                    {esDominante && (
                      <span className="etiqueta-mini">{t("inicio.elTuyo", "El tuyo")}</span>
                    )}
                    {esAcento && (
                      <span className="etiqueta-mini">{t("inicio.tambien", "También")}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {dominante && acentos.length < 2 && (
              <p className="tenue texto-mini">
                {t("inicio.sumaAcentos", "Si quieres, suma hasta dos más que también te representen.")}
              </p>
            )}
          </>
        )}

        <div className="primer-uso-acciones">
          {paso > 0 && (
            <button
              type="button"
              className="boton-texto"
              onClick={() => setPaso(paso - 1)}
              disabled={guardando}
            >
              {t("inicio.atras", "Atrás")}
            </button>
          )}

          {paso < PASOS.length - 1 ? (
            <button
              type="button"
              className="boton-primario"
              onClick={() => setPaso(paso + 1)}
              disabled={!puedeSeguir}
            >
              {t("inicio.seguir", "Seguir")}
            </button>
          ) : (
            <button
              type="button"
              className="boton-primario"
              onClick={() => terminar(false)}
              disabled={!puedeSeguir || guardando}
            >
              {guardando
                ? t("inicio.guardando", "Guardando...")
                : t("inicio.empezar", "Empezar")}
            </button>
          )}
        </div>

        {/* Saltear es visible, no escondido. Alguien que no quiere
            contestar va a encontrar la forma igual; esconderlo solo
            hace que la busque enojado.

            Se dice QUÉ se pierde, en vez de un "saltar" pelado: es la
            diferencia entre una decisión y un click a ciegas. */}
        <button
          type="button"
          className="boton-texto primer-uso-saltear"
          onClick={() => terminar(true)}
          disabled={guardando}
        >
          {t("inicio.despues", "Después lo hago")}
        </button>
        {/* "Se configura después en Perfil" ERA MENTIRA hasta hoy
            (2026-08-24): el país y las ocasiones sí estaban ahí, pero el
            guardarropa y el estilo NO existían en ninguna pantalla, y
            esta bienvenida no vuelve nunca. La promesa ahora es cierta
            —los cuatro campos están en Perfil— y por eso el texto puede
            quedarse. Si alguien saca alguno de Perfil, este texto vuelve
            a mentir. */}
        <p className="tenue texto-mini primer-uso-nota">
          {t("inicio.notaSaltear", "Si lo saltas, la app arranca con seis ocasiones genéricas y sin saber en qué hemisferio estás. Se configura todo después en Perfil.")}
        </p>
      </div>
    </div>
  );
}
